import { action, observable, runInAction, makeObservable, computed } from 'mobx'
import {
    getDistributionSummaryLink,
    getDspRates,
    getLatestRoyaltiesDate,
    getRateDsps,
    getRatePeriods,
    getRoyalties,
    getStatements,
} from '@App/api/routes/rightsholder/statements'
import RootStore from '@App/stores/root-store'
import { PayeeRoyalties } from '@Entities/statements/payee-royalties'
import { Statement } from '@Entities/statements/statement'
import { LatestRoyaltiesDate } from '@Entities/statements/latest-royalties-date'
import { SelectOption } from '@Components/Selectors/Select'
import { handleAbortedException } from '@App/helpers/handle-aborted-exception'
import dayjs from 'dayjs'
import { RateSheetUsagePeriod } from '@Entities/statements/rate-sheet-usage-period'
import { RateSheetDSP } from '@Entities/statements/rate-sheet-dsp'
import { RateSheetCollection } from '@Entities/statements/rate-sheet-collection'
import { getUsagePeriod } from '@App/helpers/work-entities/get-usage-period'

export default class StatementsStore {
    private rootStore: RootStore

    private _dspRates: RateSheetCollection[] | null

    private _ratePeriods: RateSheetUsagePeriod[] | null

    private _rateDsps: RateSheetDSP[] | null

    private _royalties: PayeeRoyalties[] | null

    private _latestRoyaltiesDate: LatestRoyaltiesDate | null | undefined

    private _statements: Statement[] = []

    private _selectedUsagePeriod: SelectOption

    private _distributionSummaryPdfLink: string | null

    constructor(rootStore: RootStore) {
        makeObservable<
            StatementsStore,
            | '_dspRates'
            | '_ratePeriods'
            | '_rateDsps'
            | '_royalties'
            | '_latestRoyaltiesDate'
            | '_statements'
            | '_selectedUsagePeriod'
            | '_distributionSummaryPdfLink'
        >(this, {
            _dspRates: observable,
            _ratePeriods: observable,
            _rateDsps: observable,
            _royalties: observable,
            _latestRoyaltiesDate: observable,
            _statements: observable,
            _selectedUsagePeriod: observable,
            _distributionSummaryPdfLink: observable,
            clearRoyalties: action,
            clearDspRates: action,
            clearStatements: action,
            clearFilters: action,
            royaltiesUsagePeriodOptions: computed,
            rateSheetUsagePeriodOptions: computed,
            rateSheetDspOptions: computed,
            setUsagePeriod: action,
        })

        this.rootStore = rootStore
    }

    get dspRates() {
        return this._dspRates
    }

    get ratePeriods() {
        return this._ratePeriods
    }

    get rateDsps() {
        return this._rateDsps
    }

    get royalties() {
        return this._royalties
    }

    get latestRoyaltiesDate() {
        return this._latestRoyaltiesDate
    }

    get statements() {
        return this._statements
    }

    get selectedUsagePeriod() {
        return this._selectedUsagePeriod
    }

    get distributionSummaryPdfLink(): string | null {
        return this._distributionSummaryPdfLink
    }

    get rateSheetUsagePeriodOptions(): SelectOption[] {
        return this._ratePeriods
            ? this._ratePeriods?.map((period, idx) => ({
                  label: period.periodName,
                  value: period.period,
                  id: idx.toString(),
              }))
            : []
    }

    get rateSheetDspOptions(): SelectOption[] {
        return this._rateDsps
            ? [
                  { label: 'All', value: 'All', id: 'all' },
                  ...this._rateDsps
                      .map((dsp, idx) => ({
                          label: dsp.dspName,
                          value: dsp.name,
                          id: idx.toString(),
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
              ]
            : []
    }

    get royaltiesUsagePeriodOptions(): SelectOption[] {
        let options = [{ label: 'April 2021', value: '04-2021', id: '1' }]
        const latestDate = this._latestRoyaltiesDate?.latestCompletedDistributionPeriodStartDate ?? ''
        if (latestDate !== '') {
            let timestamp = dayjs(latestDate, 'YYYY-MM-DD')
            let idCount = 1

            while (timestamp.format('MM-YYYY') !== '04-2021') {
                idCount++
                options.push({
                    label: timestamp.format('MMMM YYYY'),
                    value: timestamp.format('MM-YYYY'),
                    id: idCount.toString(),
                })

                timestamp = timestamp.subtract(1, 'month')
            }
            options = options.sort((a: any, b: any) =>
                dayjs(b.value, 'MM-YYYY').isBefore(dayjs(a.value, 'MM-YYYY')) ? -1 : 1
            )
        }
        return options
    }

    public loadDspRates = async (year: string, month: string, dsp?: string): Promise<void> => {
        const response = await getDspRates(year, month, dsp)
        runInAction(() => (this._dspRates = response.data))
    }

    public loadRoyalties = async (year: string, month?: string, signal?: AbortSignal): Promise<void> => {
        try {
            const response = await getRoyalties(
                this.rootStore.rightsholderStore.currentRightsholderId,
                year,
                month,
                signal
            )
            runInAction(() => (this._royalties = response.data.sort((a, b) => (a.payeeName < b.payeeName ? -1 : 1))))
        } catch (e: any) {
            handleAbortedException(e)
        }
    }

    public loadRateSheetPeriods = async () => {
        const response = await getRatePeriods()
        runInAction(() => (this._ratePeriods = response.data))
    }

    public loadRateSheetDsps = async () => {
        const response = await getRateDsps()
        runInAction(() => (this._rateDsps = response.data))
    }

    public loadLatestRoyaltiesDate = async (): Promise<void> => {
        const response = await getLatestRoyaltiesDate(this.rootStore.rightsholderStore.currentRightsholderId)
        runInAction(() => (this._latestRoyaltiesDate = response.data))
    }

    public loadStatements = async (payee: string): Promise<void> => {
        const response = await getStatements(this.rootStore.rightsholderStore.currentRightsholderId, payee)
        runInAction(() => (this._statements = response.data))
    }

    public loadDistributionSummaryPdfLink = async (year: string, month: string): Promise<void> => {
        try {
            const response = await getDistributionSummaryLink(
                this.rootStore.rightsholderStore.currentRightsholderId,
                year,
                month
            )
            runInAction(() => (this._distributionSummaryPdfLink = response.data.downloadLink))
        } catch (ignore) {
            runInAction(() => (this._distributionSummaryPdfLink = null))
        }
    }

    public clearRoyalties = (): void => {
        this._royalties = null
        this._distributionSummaryPdfLink = null
    }

    public clearDspRates = (): void => {
        this._dspRates = null
    }

    public clearStatements = (): void => {
        this._statements = []
    }

    public clearFilters = (): void => {
        this._selectedUsagePeriod = { id: '', label: '', value: '' }
        this._latestRoyaltiesDate = undefined
    }

    public loadRoyaltiesForPeriod = async (usagePeriod: SelectOption) => {
        this.setUsagePeriod(usagePeriod)
        const { year, month } = getUsagePeriod(usagePeriod)

        this.clearRoyalties()

        await Promise.all([this.loadRoyalties(year, month), this.loadDistributionSummaryPdfLink(year, month)])
    }

    public setUsagePeriod = (value: SelectOption) => {
        this._selectedUsagePeriod = value
    }
}
